import {useMutation} from '@apollo/react-hooks';

import type {User} from 'nutshell-core/users/users-types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    AddAttachmentToContact as AddAttachmentToContactMutation,
    AddAttachmentToContactVariables,
    ContactFileAddInput,
    FileFragment,
} from 'nutshell-graphql-types';

import {getFileType} from '../../../../../../core-entity/widgets/attachments-widget/helpers';

import ADD_ATTACHMENT_TO_CONTACT from '../mutations/add-attachment-to-contact.graphql';

export const useAddAttachmentToContact = (): {
    addAttachmentToContact: (
        arg1: ContactFileAddInput,
        arg2?: User | null | undefined,
        arg3?: FileFragment[] | null | undefined
    ) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [addAttachmentToContact, {loading, error}] = useMutation<
        AddAttachmentToContactMutation,
        AddAttachmentToContactVariables
    >(ADD_ATTACHMENT_TO_CONTACT);

    return {
        addAttachmentToContact: (
            input: ContactFileAddInput,
            user?: User | null,
            existingFiles?: FileFragment[] | null
        ) => {
            let mutationReturn = null;

            if (user && existingFiles) {
                mutationReturn = addAttachmentToContact({
                    variables: {input},
                    // @ts-expect-error
                    optimisticResponse: () => {
                        const newFiles = input.files.map((file) => ({
                            __typename: 'EntityAttributeValueFile',
                            fileId: String(Date.now()) + String(Math.random()),
                            href: file.locationUri,
                            viewHref: file.locationUri,
                            viewableInBrowser: false,
                            fileType: getFileType(file.mimeType, file.filename),
                            uploadedTime: Math.floor(Date.now() / 1000),
                            name: file.filename,
                            creatorUser: {
                                __typename: 'User',
                                id: user.id,
                                name: user.name,
                                avatarUrl: user.avatarUrl,
                                initials: user.initials,
                            },
                        }));

                        return {
                            contactFilesAdd: {
                                __typename: 'ContactFileAddPayload',
                                contact: {
                                    __typename: 'Contact',
                                    id: input.contact,
                                    files: [...newFiles.reverse(), ...existingFiles],
                                },
                            },
                        };
                    },
                });
            } else {
                mutationReturn = addAttachmentToContact({
                    variables: {input},
                });
            }

            return mutationReturn;
        },
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
